import React, { useState, useEffect, useRef } from 'react';

interface GLAccountOption {
  number: string;
  name: string;
  description?: string;
  category?: string;
  subcategory?: string;
}

interface SearchableGLAccountDropdownProps {
  accounts: GLAccountOption[];
  value: string;
  onChange: (accountNumber: string) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  isPredicted?: boolean;
  confidence?: number;
  onConfirm?: () => void;
  forceSearchMode?: boolean; // When true, always shows search input even when value is selected
}

const SearchableGLAccountDropdown: React.FC<SearchableGLAccountDropdownProps> = ({
  accounts,
  value,
  onChange,
  placeholder = 'Search GL accounts...',
  className = '',
  disabled = false,
  isPredicted = false,
  confidence = 0,
  onConfirm,
  forceSearchMode = false
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [filteredAccounts, setFilteredAccounts] = useState<GLAccountOption[]>([]);
  const dropdownRef = useRef<HTMLDivElement>(null);
  
  // Selected account display value
  const selectedAccount = accounts.find(acc => acc.number === value);
  const displayValue = selectedAccount 
    ? `${selectedAccount.number} - ${selectedAccount.name}`
    : '';

  // Filter accounts based on search term
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredAccounts(accounts);
      return;
    }
    
    const searchLower = searchTerm.toLowerCase();
    const filtered = accounts.filter(account => {
      const nameMatch = account.name.toLowerCase().includes(searchLower);
      const numberMatch = account.number.toLowerCase().includes(searchLower);
      const descriptionMatch = account.description?.toLowerCase().includes(searchLower) || false;
      
      return nameMatch || numberMatch || descriptionMatch;
    });
    
    // Sort results by relevance
    filtered.sort((a, b) => {
      // Exact matches come first
      const aExactNameMatch = a.name.toLowerCase() === searchLower;
      const bExactNameMatch = b.name.toLowerCase() === searchLower;
      
      if (aExactNameMatch && !bExactNameMatch) return -1;
      if (!aExactNameMatch && bExactNameMatch) return 1;
      
      // Then starts with matches
      const aStartsWithMatch = a.name.toLowerCase().startsWith(searchLower);
      const bStartsWithMatch = b.name.toLowerCase().startsWith(searchLower);
      
      if (aStartsWithMatch && !bStartsWithMatch) return -1;
      if (!aStartsWithMatch && bStartsWithMatch) return 1;
      
      return 0;
    });
    
    setFilteredAccounts(filtered);
  }, [searchTerm, accounts]);
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  
  const handleSelect = (accountNumber: string) => {
    onChange(accountNumber);
    setSearchTerm('');
    setIsOpen(false);
  };

  const handleInputClick = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setIsOpen(true); // Open dropdown when typing
  };
  
  // Show selected account or search input
  // When forceSearchMode is true, always show search input even when value is selected
  const showSelected = value && selectedAccount && !forceSearchMode;
  
  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {/* Selected account display with change option */}
      {showSelected ? (
        <div className="flex flex-col">
          {/* Prediction banner if applicable */}
          {isPredicted && (
            <div className={`mb-2 p-2 rounded-md ${onConfirm ? 'bg-yellow-50 border border-yellow-200' : 'bg-green-50 border border-green-200'}`}>
              <div className="flex items-center justify-between">
                <div>
                  <span className="text-sm font-medium">
                    {onConfirm ? 'Predicted GL Account' : 'Confirmed GL Account'}
                  </span>
                  {confidence > 0 && (
                    <span className="ml-2 text-xs text-gray-500">
                      {Math.round(confidence)}% confidence
                    </span>
                  )}
                </div>
                {onConfirm && (
                  <button
                    type="button"
                    onClick={onConfirm}
                    className="py-1 px-3 text-xs font-medium text-green-700 bg-green-100 rounded-md hover:bg-green-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                  >
                    Confirm
                  </button>
                )}
              </div>
            </div>
          )}
          
          {/* Selected account display */}
          <div className="flex items-center justify-between p-2 border rounded-md">
            <div className="flex flex-col">
              <div className="font-medium">{displayValue}</div>
              {selectedAccount?.description && (
                <div className="text-xs text-gray-500">{selectedAccount.description}</div>
              )}
              {selectedAccount?.category && (
                <div className="text-xs text-gray-400">
                  {selectedAccount.category}{selectedAccount.subcategory ? ` › ${selectedAccount.subcategory}` : ''}
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={() => {
                setSearchTerm('');
                setIsOpen(true);
              }}
              className="ml-2 p-1 text-xs text-blue-600 hover:text-blue-800 focus:outline-none"
            >
              Change
            </button>
          </div>
        </div>
      ) : (
        <>
          {/* Input for searching */}
          <div className="flex items-center relative">
            <input
              type="text"
              value={searchTerm}
              onChange={handleInputChange}
              onClick={handleInputClick}
              placeholder={placeholder}
              disabled={disabled}
              className="w-full py-3 px-4 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base"
            />
          </div>
          
          {/* Search hint text */}
          <div className="mt-1 text-xs text-gray-500">
            Search by GL number, name, or description
          </div>
        </>
      )}
      
      {/* Dropdown results */}
      {isOpen && (
        <div className="absolute z-50 mt-1 w-full min-w-[480px] bg-white shadow-lg max-h-60 rounded-md py-1 text-base overflow-auto focus:outline-none sm:text-sm border border-gray-200">
          {filteredAccounts.length > 0 ? (
            <ul className="divide-y divide-gray-200">
              {filteredAccounts.map((account) => (
                <li
                  key={account.number}
                  className={`cursor-pointer hover:bg-gray-50 px-4 py-3 transition-colors duration-150 ${
                    account.number === value ? 'bg-blue-50 border-l-4 border-blue-500' : ''
                  }`}
                  onClick={() => handleSelect(account.number)}
                >
                  <div className="flex flex-col space-y-1">
                    <div className="font-semibold text-gray-900 whitespace-normal break-words text-sm">
                      {account.number} - {account.name}
                    </div>
                    {account.description && (
                      <div className="text-sm text-gray-600 whitespace-normal break-words">
                        {account.description}
                      </div>
                    )}
                    {account.category && (
                      <div className="text-xs text-gray-500 font-medium">
                        {account.category}{account.subcategory ? ` › ${account.subcategory}` : ''}
                      </div>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <div className="px-3 py-2 text-sm text-gray-500">
              No accounts found
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchableGLAccountDropdown;
